import { Box, CircularProgress } from '@mui/material';
import { useState } from 'react';
import {
  ButtonWrapper,
  FilterColumn,
  FiltersContainer,
  RoundedButton
} from './styled';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import CustomDatePicker from '../../components/DatePicker';
import ToastMessage from '../../components/ToastMessage';
import LCReportTable from './components/LCReportTable';
import DropdownSelect from '../../components/DropdownSelect';
import { SnackbarSeverityType } from '../../enums';
import dayjs, { Dayjs } from 'dayjs';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import report, { fetchStudentLCReportFilter } from '../../apis/report';
import { transformStudentLcReportData } from './utils/transformStudentLcReport';
import { downloadReport } from '../../utils/reportUtils';
import moment from 'moment';
import { isEmpty } from 'lodash';
import { PaperWrapper, Title } from '../common/styled';

const LCReportPage = () => {
  const { user: loggedInUser } = useSelector(
    (state: RootState) => state.profile
  );
  const [selectedSchool, setSelectedSchool] = useState(
    loggedInUser.school || ''
  );

  const [fromDate, setFromDate] = useState<Dayjs | null>(dayjs());
  const [toDate, setToDate] = useState<Dayjs | null>(dayjs());
  const [hasSearched, setHasSearched] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const [studentLcReportData, setStudentLcReportData] = useState([]);

  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastSeverity, setToastSeverity] = useState<SnackbarSeverityType>(
    SnackbarSeverityType.success
  );

  const handleCloseToast = () => {
    setToastOpen(false);
  };

  const showToast = (message: string, severity: SnackbarSeverityType) => {
    setToastMessage(message);
    setToastSeverity(severity);
    setToastOpen(true);
  };

  const handleSearch = async () => {
    setIsSearching(true);
    setStudentLcReportData([]);
    try {
      const params: fetchStudentLCReportFilter = {
        school: selectedSchool,
        fromDate: fromDate
          ? moment(fromDate.toString()).format('YYYY-MM-DD')
          : '',
        toDate: toDate ? moment(toDate.toDate()).format('YYYY-MM-DD') : ''
      };

      const response = await report.fetchStudentLCReport(params);
      const transformedStudentLcData = transformStudentLcReportData(
        response as any
      );

      setStudentLcReportData(transformedStudentLcData as any);
      showToast(
        'Successfully fetched student lc reports',
        SnackbarSeverityType.success
      );
    } catch (error: any) {
      showToast(
        error.errors?.[0].message || 'Error fetching student lc reports',
        SnackbarSeverityType.error
      );
    } finally {
      setIsSearching(false);
      setHasSearched(true);
    }
  };

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      const params: fetchStudentLCReportFilter = {
        school: selectedSchool,
        fromDate: fromDate
          ? moment(fromDate.toString()).format('YYYY-MM-DD')
          : '',
        toDate: toDate ? moment(toDate.toDate()).format('YYYY-MM-DD') : ''
      };
      const response = await report.fetchStudentLCReport(params);
      const transformedStudentLcData = transformStudentLcReportData(
        response as any,
        true
      );

      if (isEmpty(transformedStudentLcData)) {
        showToast(
          'There is no data available to download as an Excel file.',
          SnackbarSeverityType.error
        );
        return;
      }

      const fileName = `Students LC Report-${moment().format('DD-MM-YYYY')}`;

      downloadReport(
        transformedStudentLcData,
        fileName,
        'Students LC Report',
        showToast
      );

      showToast('Report downloaded successfully', SnackbarSeverityType.success);
    } catch (error: any) {
      showToast(
        error.errors?.[0].message || 'Error downloading pending LC report',
        SnackbarSeverityType.error
      );
    } finally {
      setIsDownloading(false);
    }
  };

  const schoolListOptions = isEmpty(loggedInUser.schools)
    ? [{ label: loggedInUser.school, value: loggedInUser.school }]
    : loggedInUser.schools.map((school: string) => ({
        label: school,
        value: school
      }));

  const showSchoolSelector = loggedInUser.school !== 'XAVIER-KORBA';

  return (
    <Box>
      <Title>LC Report</Title>
      <PaperWrapper sx={{ minHeight: 'auto' }}>
        <FiltersContainer>
          {showSchoolSelector && (
            <FilterColumn>
              <DropdownSelect
                label='School'
                placeholder='Select School'
                name='school'
                value={selectedSchool}
                options={schoolListOptions}
                onChange={(event) => {
                  setSelectedSchool(event.target.value as string);
                  setHasSearched(false);
                }}
                hasTopLabel
                sx={{ width: '170px' }}
              />
            </FilterColumn>
          )}
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <FilterColumn>
              <CustomDatePicker
                label='LC Requested From'
                onChange={(newValue: any) => {
                  setFromDate(newValue);
                  setHasSearched(false);
                }}
                value={fromDate}
              />
            </FilterColumn>
            <FilterColumn>
              <CustomDatePicker
                label='LC Requested To'
                onChange={(newValue: any) => {
                  setToDate(newValue);
                  setHasSearched(false);
                }}
                value={toDate}
              />
            </FilterColumn>
          </LocalizationProvider>
        </FiltersContainer>
        <ButtonWrapper>
          <RoundedButton onClick={handleSearch} disabled={isSearching}>
            {isSearching ? (
              <CircularProgress size={24} color='inherit' />
            ) : (
              'View Report'
            )}
          </RoundedButton>
          <RoundedButton
            onClick={handleDownload}
            disabled={isDownloading || isSearching}
          >
            {isDownloading ? (
              <CircularProgress size={24} color='inherit' />
            ) : (
              'Download Report'
            )}
          </RoundedButton>
        </ButtonWrapper>
      </PaperWrapper>
      {hasSearched && !isSearching && (
        <LCReportTable lcReportData={studentLcReportData} />
      )}
      <ToastMessage
        open={toastOpen}
        message={toastMessage}
        severity={toastSeverity}
        onClose={handleCloseToast}
      />
    </Box>
  );
};

export default LCReportPage;
